// @ts-strict-ignore
import { Observable, Subject } from 'rxjs';
import { Camera, ColorManagement, LinearSRGBColorSpace, Scene, WebGLCapabilities, WebGLRenderer } from 'three';

export class Renderer {
    private canvas: HTMLCanvasElement;
    private scene: Scene;
    private camera: Camera;
    private renderer: WebGLRenderer;

    private rendererChanged: Subject<WebGLRenderer> = new Subject<WebGLRenderer>();
    public get rendererChanged$(): Observable<WebGLRenderer> {
        return this.rendererChanged.asObservable();
    }

    private _autoRenderEnabled: boolean = false;
    private _view3DDirty: boolean = false;
    private animationLoopHandler = () => this.animationLoop();

    public get domElement(): HTMLElement {
        return this.renderer.domElement;
    }

    public get capabilities(): WebGLCapabilities {
        return this.renderer.capabilities;
    }

    public get webGLRenderer() {
      return this.renderer;
    }

    public constructor() {
      ColorManagement.enabled = false;
      this.renderer = new WebGLRenderer({antialias: true});
      this.renderer.outputColorSpace = LinearSRGBColorSpace;
      this.rendererChanged.next(this.renderer);
    }

    public init(canvas: HTMLCanvasElement, scene: Scene, camera: Camera) {
      this.canvas = canvas;
      this.scene = scene;
      this.camera = camera;

      if (this.renderer) {
        this.renderer.forceContextLoss();
        this.renderer.domElement = undefined;
        this.renderer.dispose();
      }
      this.renderer = new WebGLRenderer({canvas: this.canvas, antialias: true});
      this.renderer.outputColorSpace = LinearSRGBColorSpace;
      this.renderer.setClearColor(0xffffff);
      this.renderer.sortObjects = true;
      this.renderer.setPixelRatio(1);
      this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
      this.renderer.domElement.addEventListener('click', (event) => {
        this.renderer.domElement.focus();
      });
      this.rendererChanged.next(this.renderer);
      this.startAnimation();
    }

    private startAnimation(): void {
        this.render(); // first scene render

        // start request animation loop frame to render scene when it's needed
        this.requestAnimationLoopFrame();
    }

    private requestAnimationLoopFrame(): void {
        requestAnimationFrame(this.animationLoopHandler);
    }

    private animationLoop(): void {
        this.onAnimationFrame();
        this.requestAnimationLoopFrame();
    }

    private onAnimationFrame(): void {
        let executeRender: boolean = false;
        if (this._autoRenderEnabled) {
            executeRender = true;
        } else if (this._view3DDirty) {
            executeRender = true;
        }
        if (executeRender) {
            this.render();
            this._view3DDirty = false;
        }
    }

    private render(): void {
        this.renderer.render(this.scene, this.camera);
    }

    /**
     * Marks 3D-scene to be rendered on the next animation frame.
     */
    public invalidate3DView(): void {
        this._view3DDirty = true;
    }

    public setSize(width: number, height: number): void {
        this.renderer.setSize(width, height);
    }
}
